import { useState, useEffect, useCallback, useRef } from 'react';
import type { User, Setor, Processo } from '../../../models';
import { apiService } from '../services/api';

export const useUsuarios = (autoFetchAll: boolean = true) => {
  const [usuarios, setUsuarios] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialFetchRef = useRef(false);

  const fetchUsuarios = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getUsuarios();
      setUsuarios(data);
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
      setError(err instanceof Error ? err.message : 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsuariosdoSetor = useCallback(async (setorId: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getUsuariosPorSetor(setorId);
      setUsuarios(data);
    } catch (err) {
      console.error('Erro ao carregar usuários do setor:', err);
      setError(err instanceof Error ? err.message : 'Erro ao carregar usuários do setor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!autoFetchAll) {
      return;
    }

    if (initialFetchRef.current) {
      return;
    }

    initialFetchRef.current = true;
    fetchUsuarios();
  }, [autoFetchAll, fetchUsuarios]);

  return { usuarios, loading, error, refetch: fetchUsuarios, fetchUsuariosdoSetor };
};

export const useSetores = () => {
  const [setores, setSetores] = useState<Setor[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialFetchRef = useRef(false);

  const fetchSetores = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getSetores();
      setSetores(data);
    } catch (err) {
      console.error('Erro ao carregar setores:', err);
      setError(err instanceof Error ? err.message : 'Erro ao carregar setores');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initialFetchRef.current) {
      return;
    }

    initialFetchRef.current = true;
    fetchSetores();
  }, [fetchSetores]);

  return { setores, loading, error, refetch: fetchSetores };
};

export const useProcessos = () => {
  const [processos, setProcessos] = useState<Processo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialFetchRef = useRef(false);

  const fetchProcessos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getProcessos();
      setProcessos(data);
    } catch (err) {
      console.error('Erro ao carregar processos:', err);
      setError(err instanceof Error ? err.message : 'Erro ao carregar processos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initialFetchRef.current) {
      return;
    }

    initialFetchRef.current = true;
    fetchProcessos();
  }, [fetchProcessos]);

  return { processos, loading, error, refetch: fetchProcessos };
};

export const useUsuarioAtual = () => {
  const [usuario, setUsuario] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUsuarioAtual = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getUsuarioAtual();
      setUsuario(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar usuário atual');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsuarioAtual();
  }, []);

  return { usuario, loading, error, refetch: fetchUsuarioAtual };
};

export const useMeusSetores = () => {
  const [setores, setSetores] = useState<Setor[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialFetchRef = useRef(false);

  const fetchMeusSetores = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getMeusSetores(forceRefresh);
      setSetores(data);
    } catch (err) {
      console.error('Erro ao carregar meus setores:', err);
      setError(err instanceof Error ? err.message : 'Erro ao carregar meus setores');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initialFetchRef.current) {
      return;
    }

    initialFetchRef.current = true;
    fetchMeusSetores();
  }, [fetchMeusSetores]);

  return { setores, loading, error, refetch: fetchMeusSetores };
};

export const useSetorLogado = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const definirSetor = async (setorId: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.definirSetorLogado(setorId);
      return response;
    } catch (erro) {
      console.error('Erro ao definir setor logado:', erro);
      const errorMessage = erro instanceof Error ? erro.message : 'Erro ao definir setor logado';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return { definirSetor, loading, error };
};